import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Loader2,
  Download,
  FileText,
  Image as ImageIcon,
  Film,
  Music,
  Archive,
  File as FileIcon,
  AlertCircle,
  Eye,
  ExternalLink,
} from "lucide-react";
import { ASSETS } from "@/lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtBytes(bytes) {
  if (!bytes && bytes !== 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function iconFor(ctype) {
  if (!ctype) return FileIcon;
  if (ctype.startsWith("image/")) return ImageIcon;
  if (ctype.startsWith("video/")) return Film;
  if (ctype.startsWith("audio/")) return Music;
  if (ctype.includes("zip")) return Archive;
  return FileText;
}

export default function LibraryShare() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | expired | notfound | error
  const [errMsg, setErrMsg] = useState("");
  const [thumbUrl, setThumbUrl] = useState(null);
  const [showViewer, setShowViewer] = useState(false);
  const [viewerBlobUrl, setViewerBlobUrl] = useState(null);
  const [viewerLoading, setViewerLoading] = useState(false);

  useEffect(() => {
    document.title = "Shared file · Great Rate Check";
    const metaEl = document.head.querySelector('meta[name="robots"]');
    const el = metaEl || document.createElement("meta");
    el.setAttribute("name", "robots");
    el.setAttribute("content", "noindex,nofollow");
    if (!metaEl) document.head.appendChild(el);

    async function load() {
      try {
        const res = await fetch(`${API}/library/share/${token}/meta`);
        if (res.status === 410) {
          setStatus("expired");
          return;
        }
        if (!res.ok) {
          setStatus("notfound");
          return;
        }
        const data = await res.json();
        setMeta(data);
        setStatus("ok");
      } catch (e) {
        setStatus("error");
        setErrMsg("Network error. Please retry.");
      }
    }
    load();
  }, [token]);

  // Load thumbnail
  useEffect(() => {
    if (!meta) return;
    const isImage = (meta.content_type || "").startsWith("image/");
    if (!isImage && !meta.has_thumb) return;
    const src = isImage
      ? `${API}/library/share/${token}/preview`
      : `${API}/library/share/${token}/thumb`;
    let revoked = false;
    let url = null;
    (async () => {
      try {
        const res = await fetch(src);
        if (!res.ok) return;
        const blob = await res.blob();
        if (revoked) return;
        url = URL.createObjectURL(blob);
        setThumbUrl(url);
      } catch (e) {
        console.debug("Thumb load failed:", e?.message);
      }
    })();
    return () => {
      revoked = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [meta, token]);

  const download = () => {
    window.location.href = `${API}/library/share/${token}/download`;
  };

  const openViewer = async () => {
    if (!meta) return;
    setShowViewer(true);
    // Image / PDF / video / audio → fetch as blob URL to feed to iframe/video/etc.
    const ctype = meta.content_type || "";
    const isBlobViewable =
      ctype.startsWith("image/") ||
      ctype === "application/pdf" ||
      /\.pdf$/i.test(meta.filename || "") ||
      ctype.startsWith("video/") ||
      ctype.startsWith("audio/");
    if (!isBlobViewable) return; // Office viewer uses public URL directly
    setViewerLoading(true);
    try {
      const res = await fetch(`${API}/library/share/${token}/preview`);
      if (!res.ok) throw new Error("preview failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setViewerBlobUrl(url);
    } catch (e) {
      console.warn("Preview load failed; showing fallback prompt:", e?.message);
    } finally {
      setViewerLoading(false);
    }
  };

  const closeViewer = () => {
    if (viewerBlobUrl) URL.revokeObjectURL(viewerBlobUrl);
    setViewerBlobUrl(null);
    setShowViewer(false);
  };

  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <div className="inline-flex items-center gap-2 text-[#59564C]">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading…
        </div>
      </div>
    );
  }

  if (status !== "ok") {
    const message =
      status === "expired"
        ? "This share link has expired. Ask the sender for a new one."
        : status === "notfound"
        ? "This share link is invalid or the file was removed."
        : errMsg || "Something went wrong. Please retry.";
    return (
      <div
        className="min-h-screen flex items-center justify-center px-6"
        style={{
          background:
            "radial-gradient(1000px 500px at 20% -10%, #17385e 0%, #0F2A47 50%, #08192F 100%)",
        }}
      >
        <div className="max-w-md w-full bg-white rounded-3xl p-8 shadow-2xl text-center">
          <AlertCircle className="w-10 h-10 text-[#B04A2E] mx-auto" />
          <h1 className="mt-4 text-xl font-semibold text-[#0F2A47]">Link unavailable</h1>
          <p className="mt-2 text-sm text-[#59564C]">{message}</p>
          <a
            href="/"
            className="inline-block mt-6 rounded-xl bg-[#0F2A47] hover:bg-[#173a5f] text-white px-5 py-2.5 text-sm"
          >
            Go to Great Rate Check
          </a>
        </div>
      </div>
    );
  }

  const ctype = meta.content_type || "";
  const isImage = ctype.startsWith("image/");
  const isPdf = ctype === "application/pdf" || /\.pdf$/i.test(meta.filename || "");
  const isVideo = ctype.startsWith("video/");
  const isAudio = ctype.startsWith("audio/");
  const isOffice =
    ctype.includes("wordprocessingml") ||
    ctype.includes("presentationml") ||
    ctype.includes("spreadsheetml") ||
    /\.(docx|pptx|xlsx)$/i.test(meta.filename || "");
  const canView = isImage || isPdf || isVideo || isAudio || isOffice;

  const Icon = iconFor(ctype);
  const ext = (meta.filename?.split(".").pop() || "").toUpperCase().slice(0, 5);

  // Absolute public URL to the share-download endpoint (Office viewer needs this)
  const publicDownloadUrl = `${API}/library/share/${token}/download`;
  const officeSrc = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
    publicDownloadUrl,
  )}`;

  return (
    <div
      className="min-h-screen"
      style={{
        background:
          "linear-gradient(180deg, #08192F 0%, #0F2A47 40%, #FDFBF7 40%)",
      }}
      data-testid="library-share-page"
    >
      <header className="max-w-3xl mx-auto px-6 pt-10 pb-6 flex items-center justify-between">
        <a href="/" className="inline-flex bg-white rounded-2xl px-3 py-1.5 shadow-soft">
          <img src={ASSETS.logoHorizontal} alt="Great Rate Check" className="h-9 sm:h-11 w-auto" />
        </a>
        <span className="text-[11px] uppercase tracking-widest text-[#A6D24A]">
          Shared file
        </span>
      </header>

      <main className="max-w-3xl mx-auto px-6 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-[#EDE9DC]"
        >
          <button
            type="button"
            onClick={canView ? openViewer : undefined}
            disabled={!canView}
            data-testid="library-share-thumb"
            className={`w-full aspect-[16/9] bg-[#F4F2EB] flex items-center justify-center relative overflow-hidden ${
              canView ? "cursor-pointer group/thumb" : "cursor-default"
            }`}
            aria-label={canView ? `View ${meta.filename}` : meta.filename}
          >
            {thumbUrl ? (
              <img
                src={thumbUrl}
                alt={meta.filename}
                className={
                  isImage ? "w-full h-full object-cover" : "w-full h-full object-contain bg-white"
                }
              />
            ) : (
              <div className="flex flex-col items-center gap-3">
                <Icon className="w-16 h-16 text-[#7A7668]" strokeWidth={1.4} />
                <span className="text-[11px] uppercase tracking-widest text-[#7A7668]">{ext}</span>
              </div>
            )}
            {canView && (
              <div className="absolute inset-0 bg-black/0 group-hover/thumb:bg-black/30 transition-colors flex items-center justify-center">
                <span className="opacity-0 group-hover/thumb:opacity-100 transition-opacity text-white text-sm font-medium inline-flex items-center gap-1.5 bg-black/60 backdrop-blur px-4 py-2 rounded-full">
                  <Eye className="w-4 h-4" /> Click to view
                </span>
              </div>
            )}
          </button>
          <div className="p-6 sm:p-8">
            <div className="text-[11px] uppercase tracking-widest text-[#1F8F8B]">
              {meta.category === "marketing"
                ? "Marketing Asset"
                : meta.category === "messaging"
                ? "Messaging & Outreach"
                : meta.category === "process"
                ? "Process"
                : "Document"}
            </div>
            <h1 className="mt-1 text-2xl sm:text-3xl font-semibold text-[#0F2A47] break-words">
              {meta.filename}
            </h1>
            {meta.description ? (
              <p className="mt-3 text-[#59564C] leading-relaxed">{meta.description}</p>
            ) : null}
            <div className="mt-4 flex items-center gap-3 text-xs text-[#7A7668]">
              <span>{fmtBytes(meta.size)}</span>
              <span>·</span>
              <span>{ext}</span>
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              {canView && (
                <button
                  onClick={openViewer}
                  data-testid="library-share-view"
                  className="inline-flex items-center gap-2 rounded-xl bg-[#1F8F8B] hover:bg-[#177977] text-white px-6 py-3 shadow"
                >
                  <Eye className="w-4 h-4" /> View in browser
                </button>
              )}
              <button
                onClick={download}
                data-testid="library-share-download"
                className="inline-flex items-center gap-2 rounded-xl bg-[#0F2A47] hover:bg-[#173a5f] text-white px-6 py-3 shadow"
              >
                <Download className="w-4 h-4" /> Download file
              </button>
            </div>
            <p className="mt-6 text-[11px] text-[#7A7668]">
              Shared privately by Great Rate Check · NEXA Lending. Please do not redistribute.
            </p>
          </div>
        </motion.div>
      </main>

      {/* Viewer modal */}
      {showViewer && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex flex-col"
          onClick={closeViewer}
          data-testid="library-share-viewer"
        >
          <div
            className="flex items-center justify-between px-4 sm:px-6 py-3 bg-[#0F2A47] text-white"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-widest text-[#A6D24A]">Preview</div>
              <div className="text-sm sm:text-base font-semibold truncate">{meta.filename}</div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={download}
                className="inline-flex items-center gap-1.5 rounded-lg bg-white/10 hover:bg-white/20 px-3 py-1.5 text-sm"
              >
                <Download className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Download</span>
              </button>
              {isOffice && (
                <a
                  href={officeSrc}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-lg bg-white/10 hover:bg-white/20 px-3 py-1.5 text-sm"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Open full</span>
                </a>
              )}
              <button
                onClick={closeViewer}
                className="p-2 rounded-lg hover:bg-white/10"
                aria-label="Close preview"
              >
                ✕
              </button>
            </div>
          </div>
          <div
            className="flex-1 flex items-center justify-center p-2 sm:p-6 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {viewerLoading ? (
              <div className="text-white/80 inline-flex items-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin" /> Loading…
              </div>
            ) : isImage && viewerBlobUrl ? (
              <img
                src={viewerBlobUrl}
                alt={meta.filename}
                className="max-w-full max-h-full object-contain rounded-lg shadow-2xl bg-white"
              />
            ) : isPdf && viewerBlobUrl ? (
              <iframe
                src={viewerBlobUrl}
                title={meta.filename}
                className="w-full h-full bg-white rounded-lg shadow-2xl"
              />
            ) : isVideo && viewerBlobUrl ? (
              <video src={viewerBlobUrl} controls autoPlay className="max-w-full max-h-full rounded-lg shadow-2xl bg-black" />
            ) : isAudio && viewerBlobUrl ? (
              <div className="w-full max-w-2xl bg-white rounded-2xl p-8 shadow-2xl">
                <div className="font-semibold text-[#0F2A47] mb-3">{meta.filename}</div>
                <audio src={viewerBlobUrl} controls className="w-full" />
              </div>
            ) : isOffice ? (
              <iframe
                src={officeSrc}
                title={meta.filename}
                className="w-full h-full bg-white rounded-lg shadow-2xl"
                data-testid="library-share-office-frame"
              />
            ) : (
              <div className="text-white/80">Preview not available. Please download.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
